import React from 'react';
import { Alert, Box, Button, Chip, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import * as GeoAdConstants from './GeoAdConstants.js';
import ETVPage from '../tools/ETVPage.js';
import ETVTable from '../tools/ETVTable.js';
import ETVMessagePopup from '../tools/ETVMessagePopup.js';

class GeoAdPayments extends React.Component {

	constructor(props) {
		super(props);
		this.closeInfoMessage = this.closeInfoMessage.bind(this);
	}

	state = {
		isLoaded:false,
		isLoading:true,
		buildings:[],
		pendingPayments:[],
		paidOut:[],
		downloadingId:null,
		window:'',
		title:'',
		message:'',
		filterBuilding:'',
	};

	componentDidMount() {
		this.loadData();
	}

	getGeoAdHeaders() {
		return {
			'user_id': localStorage.getItem("etv.user_id"),
			'access_token': localStorage.getItem("etv.access_token"),
		};
	}

	loadData() {
		this.setState({isLoading:true});
		fetch(GeoAdConstants.GEOAD_BACKEND+"?action=get_payments",{ headers: this.getGeoAdHeaders() })
			.then(res => res.json())
			.then((result) => {
				if(result.result) {
					this.setState({
						buildings: result.buildings || [],
						pendingPayments: result.pending_payments || [],
						paidOut: result.paid_out || [],
						isLoaded:true,
						isLoading:false,
					});
				}else{
					this.setState({isLoaded:true,isLoading:false,buildings:[],pendingPayments:[],paidOut:[]});
					this.showErrorMessage("GeoAd payments", result.error || "Could not load payments.");
				}
			})
			.catch((error) => {
				this.setState({isLoaded:true,isLoading:false,buildings:[],pendingPayments:[],paidOut:[],error});
				this.showErrorMessage("GeoAd payments", error.message || "Could not load payments.");
			});
	}

	formatMoney(currency, amountMinor, minorUnits) {
		var decimals = Number(minorUnits || 0);
		var value = Number(amountMinor || 0) / Math.pow(10, decimals);
		return (currency || 'SGD') + ' ' + value.toFixed(decimals);
	}

	formatCampaign(row) {
		if(!row.first_slot_date) return '-';
		if(row.first_slot_date===row.last_slot_date || !row.last_slot_date) return row.first_slot_date;
		return row.first_slot_date + ' - ' + row.last_slot_date;
	}

	bookingRef(row) {
		return row.booking_reference || "BK-" + String(row.booking_id || 0).padStart(4, '0');
	}

	showErrorMessage(title, message) {
		this.setState({window:'error_message',title:title,message:message});
	}

	closeInfoMessage() {
		this.setState({window:'',title:'',message:''});
	}

	statusLabel(status) {
		return status==='SCHEDULED' ? 'Scheduled' : 'Awaiting monthly payout';
	}

	clearFilters() {
		this.setState({filterBuilding:''});
	}

	async downloadStatement(row) {
		this.setState({downloadingId:row.settlement_id});
		try {
			const response = await fetch(
				GeoAdConstants.GEOAD_BACKEND+"?action=download_payment_statement&settlement_id="+encodeURIComponent(row.settlement_id),
				{headers:this.getGeoAdHeaders()}
			);
			const result = await response.json();
			if(!result.result) throw new Error(result.error || 'Could not download the statement.');
			const binary = window.atob(result.content_base64);
			const bytes = new Uint8Array(binary.length);
			for(let index=0; index<binary.length; index++) bytes[index] = binary.charCodeAt(index);
			const url = window.URL.createObjectURL(new Blob([bytes], {type:result.content_type || 'application/pdf'}));
			const link = document.createElement('a');
			link.href = url;
			link.download = result.filename || `${row.statement_number}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch(error) {
			this.showErrorMessage('Payment statement', error.message || 'Could not download the statement.');
		} finally {
			this.setState({downloadingId:null});
		}
	}

	render() {
		var buildings = this.state.buildings || [];
		var allPending = this.state.pendingPayments || [];
		var pending = allPending.filter((row) =>
			(!this.state.filterBuilding || String(row.location_id)===String(this.state.filterBuilding))
		);
		var pendingTotals = pending.reduce((result,row) => {
			var currency = row.currency || 'SGD';
			if(!result[currency]) result[currency] = {currency,amount_minor:0,booking_count:0,currency_minor_units:row.currency_minor_units};
			result[currency].amount_minor += Number(row.amount_minor || 0);
			result[currency].booking_count += 1;
			return result;
		},{});
		const pendingColumns = [
			{title:'Booking',field:'booking_id',render:row=>this.bookingRef(row)},
			{title:'Building',field:'location_name'},
			{title:'Recipient',field:'recipient_name'},
			{title:'Slots',field:'first_slot_date',render:row=>this.formatCampaign(row)},
			{title:'Status',field:'payment_status',render:row=><Chip size="small" label={this.statusLabel(row.payment_status)} color={row.payment_status==='SCHEDULED'?'info':'warning'} variant="outlined"/>},
			{title:'Your share',field:'amount_minor',render:row=>this.formatMoney(row.currency,row.amount_minor,row.currency_minor_units),cellStyle:{textAlign:'right'},headerStyle:{textAlign:'right'}},
		];
		const paidColumns = [
			{title:'Statement',field:'statement_number',render:row=><><strong>{row.statement_number}</strong><small>{row.recipient_name}</small></>},
			{title:'Period',field:'period_start',render:row=>`${row.period_start} – ${row.period_end}`},
			{title:'Paid on',field:'payment_date',render:row=>row.payment_date || '-'},
			{title:'Bank reference',field:'bank_reference',render:row=>row.bank_reference || '-'},
			{title:'Amount',field:'amount_minor',render:row=>this.formatMoney(row.currency,row.amount_minor,row.currency_minor_units),cellStyle:{textAlign:'right'},headerStyle:{textAlign:'right'}},
			{title:'Statement PDF',field:'document_id',render:row=><Button size="small" variant="outlined" startIcon={<DownloadIcon/>} disabled={this.state.downloadingId===row.settlement_id} onClick={()=>this.downloadStatement(row)}>{this.state.downloadingId===row.settlement_id?'Preparing…':'Download PDF'}</Button>},
		];

		return (
			<ETVPage width="standard" icon={<AccountBalanceWalletIcon fontSize="large"/>} title="Payments" description="Your screen-owner revenue share and monthly payout statements." loading={this.state.isLoading}>
				<Paper variant="outlined" sx={{p:2,mb:2,bgcolor:'rgba(255,106,33,0.025)'}}>
					<Grid container spacing={1.5} alignItems="center">
						<Grid item xs={12} sm={6} md={3}>
							<TextField select fullWidth size="small" label="Building" value={this.state.filterBuilding} onChange={(e)=>this.setState({filterBuilding:e.target.value})}>
								<MenuItem value="">All buildings</MenuItem>
								{buildings.map((building)=><MenuItem key={building.location_id} value={String(building.location_id)}>{building.location_name}{building.city ? ` · ${building.city}` : ''}</MenuItem>)}
							</TextField>
						</Grid>
						{this.state.filterBuilding && <Grid item xs={12} sm="auto"><Button size="small" onClick={()=>this.clearFilters()}>Clear filter</Button></Grid>}
					</Grid>
				</Paper>

				{!this.state.isLoading && <Grid container spacing={2} sx={{mb:2}}>
					{Object.values(pendingTotals).map((item) => (
							<Grid item xs={12} sm={6} md={4} key={item.currency || 'currency'}>
								<Paper variant="outlined" sx={{p:2}}>
									<Stack direction="row" spacing={1} alignItems="center">
										<AccountBalanceWalletIcon color="warning"/>
										<Box>
											<Typography variant="caption" color="text.secondary">Pending payments</Typography>
											<Typography variant="h6" fontWeight={700}>{this.formatMoney(item.currency,item.amount_minor,item.currency_minor_units)}</Typography>
											<Chip size="small" icon={<ReceiptLongIcon/>} label={`${item.booking_count} booking${Number(item.booking_count)===1?'':'s'}`} color="warning" variant="outlined" sx={{mt:0.75}}/>
										</Box>
									</Stack>
								</Paper>
							</Grid>
						))}
				</Grid>}

				<Box sx={{mb:3}}>
					<Typography variant="h6" fontWeight={700} sx={{mb:1}}>Pending payments</Typography>
					{!this.state.isLoading && (pending.length===0
						? <Alert severity="info">{allPending.length===0?'No pending screen-owner payments yet.':'No pending payments match the selected building.'}</Alert>
						: <ETVTable title="Pending payments" data={pending} columns={pendingColumns} search={true} pageSize={10}/>
					)}
				</Box>

				<Box>
					<Typography variant="h6" fontWeight={700} sx={{mb:1}}>Paid out</Typography>
					{!this.state.isLoading && (this.state.paidOut.length===0
						? <Alert severity="info">No monthly payouts have been completed yet.</Alert>
						: <ETVTable title="Paid out" data={this.state.paidOut} columns={paidColumns} search={true} pageSize={10}/>
					)}
				</Box>

				<ETVMessagePopup open={this.state.window==='error_message'} onClose={this.closeInfoMessage} title={this.state.title} message={this.state.message} type="failure"/>
			</ETVPage>
		);
	}
}

export default GeoAdPayments;
